import { AvatarTypeToFootageRequirements } from '@/pages/avatar/common/consts';
import { useCreateAvatarStore } from '@/pages/avatar/common/context';
import { AvatarType, FootageMethod } from '@/pages/avatar/common/types';
import { RecordWithWebcam } from '@/pages/avatar/components/AvatarFootage/RecordWithWebcam';
import { ReviewFootage } from '@/pages/avatar/components/AvatarFootage/ReviewFootage';
import { UploadFootage } from '@/pages/avatar/components/AvatarFootage/UploadFootage';
import { VideoHelper } from '@/pages/avatar/components/VideoHelper';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import styled from '@emotion/styled';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';

const HideContainer = styled.div<{ hide: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: min-content;
    ${({ hide }) => hide && 'position: absolute; z-index: -1; opacity: 0; pointer-events: none;'}
`;

const MethodsContainer = styled.div<{ childCount: number }>`
    display: grid;
    grid-template-columns: repeat(${({ childCount }) => childCount}, 1fr);
    grid-gap: 52px;
    width: 824px;
`;

const ExpressOverrideMinDurationFeature = 'express:skip-min-duration';
const OverrideMinDurationInSeconds = 5;
export const AvatarFootage = observer(() => {
    const createAvatarStore = useCreateAvatarStore();
    const { user } = useAuth();

    const mixpanel = useAnalytics();
    const isWebcamEnabled = !(createAvatarStore.type === AvatarType.Express) || useFlag('express_flow_is_webcam_enabled', false);

    const footageRequirements = useMemo(() => {
        const footageRequirementsByType = AvatarTypeToFootageRequirements[createAvatarStore.type];
        if (createAvatarStore.type === AvatarType.Express && user?.features.includes(ExpressOverrideMinDurationFeature)) {
            footageRequirementsByType.minFileDurationInSeconds = OverrideMinDurationInSeconds;
        }
        return footageRequirementsByType;
    }, [user, createAvatarStore.type]);

    const handleFileChange = (file: File | null, footageMethod: FootageMethod) => {
        if (file) {
            mixpanel?.track('avatar-footage', { type: createAvatarStore.type, method: footageMethod });
        }

        if (footageMethod === FootageMethod.Footage) {
            runInAction(() => {
                createAvatarStore.setUsedFootageScript(false);
                createAvatarStore.setFootageScriptLanguage('');
            });
        }
        runInAction(() => {
            createAvatarStore.setFootageMethod(footageMethod);

            if (file) {
                createAvatarStore.handleFootage(file);
            }
        });
    };

    return (
        <>
            {createAvatarStore.isFootageProvided && <ReviewFootage />}
            <HideContainer hide={!!(createAvatarStore.footageFile || createAvatarStore.footageUrl)}>
                <MethodsContainer childCount={isWebcamEnabled ? 2 : 1}>
                    <UploadFootage
                        onFileChange={(file: File | null) => handleFileChange(file, FootageMethod.Footage)}
                        maxFileSizeInMB={footageRequirements.maxFileSizeInMB}
                        minFileDurationInSeconds={footageRequirements.minFileDurationInSeconds}
                        maxFileDurationInSeconds={footageRequirements.maxFileDurationInSeconds}
                        uploadFootageSubtext={footageRequirements.uploadFootageSubtext}
                        minResoultion={footageRequirements.minResolution}
                        isDurationCheckRequired={
                            !(
                                createAvatarStore.type === AvatarType.PremiumPlus &&
                                user?.features?.includes('premium-plus:no-duration-check')
                            )
                        }
                    />

                    {isWebcamEnabled && (
                        <RecordWithWebcam
                            badgeText={createAvatarStore.type === AvatarType.Express ? 'Great for testing' : undefined}
                            onFileChange={file => handleFileChange(file, FootageMethod.Webcam)}
                        />
                    )}
                </MethodsContainer>
                <VideoHelper />
            </HideContainer>
        </>
    );
});
